import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLoglines, saveLoglines } from '../../services/storyToolsService';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  FormControlLabel,
  Switch,
  useTheme,
  Snackbar,
  FormHelperText,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import Alert from '@mui/material/Alert';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  ContentCopy as ContentCopyIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';

// Shared suggestions
const INCIDENT_OPTIONS = [
  // Personal shocks
  'A sudden revelation',
  'A shocking betrayal by a friend',
  'An unexpected inheritance',
  'A devastating diagnosis',
  'A mysterious disappearance',
  // Crimes & mysteries
  'A crime witnessed',
  'A wrongful accusation',
  'A long-buried secret surfaces',
  'An identity stolen',
  // Discoveries & catalysts
  'A mysterious event',
  'An ancient artifact is found',
  'A portal opens to another world',
  'An experiment goes wrong',
  'A prophecy is revealed',
  // Threats & disasters
  'A natural disaster strikes',
  'War breaks out',
  'A dangerous enemy arrives in town',
  'A kidnapping shakes the community',
  'A ticking device is discovered',
  // Life turns
  'A sudden layoff',
  'A promotion with strings attached',
  'A new neighbor arrives with secrets',
  'An ultimatum is delivered'
];

const ANTAGONIST_OPTIONS = [
  // Human threats
  { label: 'A powerful crime lord', group: 'Human' },
  { label: 'A corrupt government official', group: 'Human' },
  { label: 'A ruthless CEO', group: 'Human' },
  { label: 'A rival genius', group: 'Human' },
  { label: 'A jealous colleague', group: 'Human' },
  { label: 'An obsessive stalker', group: 'Human' },
  { label: 'A warlord seeking control', group: 'Human' },
  // Non-human / abstract
  { label: 'An ancient evil', group: 'Non-Human/Abstract' },
  { label: 'A rogue AI', group: 'Non-Human/Abstract' },
  { label: 'A deadly conspiracy', group: 'Non-Human/Abstract' },
  { label: 'A natural force of destruction', group: 'Non-Human/Abstract' },
  { label: 'A spreading pandemic', group: 'Non-Human/Abstract' },
  // Internal/Institutional
  { label: 'Their own inner demons', group: 'Internal/Institutional' },
  { label: 'A corrupt institution', group: 'Internal/Institutional' },
  { label: 'A secretive cabal', group: 'Internal/Institutional' }
];

const ACTION_OPTIONS = [
  // Investigate/Reveal
  'uncover the truth',
  'solve the mystery',
  'expose a conspiracy',
  'reveal government corruption',
  'unmask the culprit',
  'decode a cryptic message',
  'trace a hidden signal',
  // Prevent/Thwart
  'stop the villain',
  'prevent a catastrophe',
  'halt an imminent attack',
  'thwart an assassination',
  'sabotage a deadly plan',
  'disarm a ticking device',
  // Rescue/Escape/Survive
  'save their family',
  'rescue hostages',
  'break out of prison',
  'escape the city',
  'survive the night',
  'cross a hostile wasteland',
  // Pursue/Chase
  'track a serial killer',
  'hunt down a traitor',
  'pursue a stolen artifact',
  // Compete/Achieve
  'win the championship',
  'win custody',
  'reclaim their legacy',
  'earn redemption',
  'prove their innocence',
  // Lead/Organize
  'assemble a team',
  'lead a rebellion',
  'infiltrate a secret organization',
  'negotiate a truce',
  'broker a fragile peace',
  // Retrieve/Protect
  'retrieve a lost artifact',
  'protect the secret',
  'protect a key witness',
  'deliver a mysterious package',
  // Science/Medical
  'find a cure',
  'contain a deadly outbreak',
  // Heist/Crime
  'stop a high-stakes heist',
  'pull off an impossible heist',
  // Personal/Training
  'train for the ultimate fight',
  'outwit a ruthless rival'
];

// AI Mode shared options
const GENRE_OPTIONS = ['Action','Adventure','Comedy','Crime','Drama','Fantasy','Historical','Horror','Mystery','Romance','Sci-Fi','Thriller','Western'];

const LOCATION_OPTIONS = [
  // Urban
  'A bustling metropolis',
  'A decaying industrial city',
  'A neon-soaked cyberpunk district',
  'A small rural town',
  'A desert border town',
  // Remote & Natural
  'A remote island',
  'A snowbound mountain village',
  'A dense rainforest outpost',
  'A windswept coastal cliffside',
  // Sci-Fi
  'A dystopian future city',
  'A space colony',
  'A deep-space research station',
  'An undersea research base',
  'A terraformed frontier world',
  // Fantasy & Historical
  'A magical fantasy world',
  'A walled medieval city',
  'An ancient desert kingdom',
  'An enchanted forest realm',
  // Institutions & Confined
  'A high-security prison complex',
  'A top-secret government facility',
  'A prestigious boarding school',
  'A sprawling hospital network',
  'An underground resistance bunker',
  // Travel & Transient
  'A luxury ocean liner',
  'A transcontinental bullet train',
  'A cross-country road trip',
  'An international airport terminal',
  // Conflict Zones
  'A war-torn borderland',
  'A besieged city under curfew',
  'A demilitarized neutral zone'
];

const PROTAGONIST_OPTIONS = [
  // Archetypes
  { label: 'A reluctant hero', group: 'Archetypes' },
  { label: 'An anti-hero', group: 'Archetypes' },
  { label: 'A chosen one', group: 'Archetypes' },
  { label: 'A fish-out-of-water', group: 'Archetypes' },
  { label: 'A cynical veteran', group: 'Archetypes' },
  { label: 'An idealistic rookie', group: 'Archetypes' },
  { label: 'A misfit outsider', group: 'Archetypes' },
  { label: 'A flawed genius', group: 'Archetypes' },
  { label: 'A single parent', group: 'Archetypes' },
  { label: 'A retired legend', group: 'Archetypes' },
  // Professions
  { label: 'A young detective', group: 'Professions' },
  { label: 'A struggling artist', group: 'Professions' },
  { label: 'A brilliant scientist', group: 'Professions' },
  { label: 'A small-town sheriff', group: 'Professions' },
  { label: 'An investigative journalist', group: 'Professions' },
  { label: 'A washed-up athlete', group: 'Professions' },
  { label: 'An ambitious lawyer', group: 'Professions' },
  { label: 'A compassionate doctor', group: 'Professions' },
  { label: 'A burned-out teacher', group: 'Professions' },
  { label: 'A hacker-for-hire', group: 'Professions' },
  // Diverse & Non-Human
  { label: 'An AI companion', group: 'Non-Human' },
  { label: 'A sentient android', group: 'Non-Human' },
  { label: 'An orphaned street kid', group: 'Background' },
  { label: 'A refugee with a secret', group: 'Background' },
  { label: 'A royal in disguise', group: 'Background' }
];

const GOAL_OPTIONS = [
  'Solve the case',
  'Win the competition',
  'Rescue a loved one',
  'Escape a dangerous situation',
  'Uncover the truth',
  'Survive against the odds',
  'Protect their family',
  'Avenge a wrongdoing',
  'Save the world',
  'Prove their innocence',
  'Reclaim their legacy',
  'Find a missing person',
  'Stop a looming catastrophe',
  'Achieve a lifelong dream'
];

const STAKES_OPTIONS = [
  // Personal
  'Lose their life',
  'Lose their family',
  'Lose their freedom',
  'Lose their career',
  'Public disgrace',
  'Be blacklisted from their industry',
  'Lose custody',
  'Be framed for the crime',
  'Exile or banishment',
  'Deportation',
  'Lose their home',
  'Lose true love',
  'Trust shattered forever',
  'Friendship destroyed',
  // Legal/Social
  'Go to prison',
  'Financial ruin',
  'Reputation ruined',
  'Permanent surveillance',
  'Labeled a traitor',
  // Secrets/Truth
  'Expose a devastating secret',
  'The truth is buried forever',
  // Community/City/World
  'Community collapses',
  'Town falls into chaos',
  'City destroyed',
  'War breaks out',
  'Apocalypse',
  'Planet destroyed',
  // Organizations/Institutions
  'Company ruined',
  'Movement crushed',
  'Team disbands',
  // High-concept/SF
  'Timeline collapses',
  'Species wiped out',
  'A rift opens between worlds',
  // Psychological/Spiritual
  'Lose their mind',
  'Soul damned',
];

const LoglineGenerator = ({ projectId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 2.5,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };
  const [loglines, setLoglines] = useState([]);
  const [currentLogline, setCurrentLogline] = useState({
    genre: '',
    protagonist: '',
    protagonistCustom: '',
    action: '',
    goal: '',
    antagonist: '',
    antagonistCustom: '',
    conflict: '',
    stakes: '',
    location: '',
    incitingIncident: '',
    incitingIncidentCustom: ''
  });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('simple');
  const [error, setError] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [formula, setFormula] = useState('f1'); // f1: Incident, Protagonist, Action, Antagonist; f2: Protagonist, Action, Goal, Stakes
  const [hasToken, setHasToken] = useState(!!localStorage.getItem('token'));
  const saveTimerRef = useRef(null);
  const isLoadedRef = useRef(false);

  const handleModeChange = (event, newMode) => {
    setMode(newMode);
  };
  const handleFormulaChange = (event, newFormula) => {
    if (newFormula) setFormula(newFormula);
  };

  // Save to API with debouncing and localStorage backup
  const saveToAPI = useCallback(async (loglinesData) => {
    const demoSession = !hasToken && projectId === 'landing';
    if (demoSession) return;
    
    // Always save to localStorage immediately
    try {
      localStorage.setItem(`loglines-${projectId}`, JSON.stringify(loglinesData));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }

    // Debounce API save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveLoglines(projectId, { loglines: loglinesData });
        console.log('✅ Loglines saved to API');
      } catch (err) {
        console.error('Error saving loglines to API (localStorage backup active):', err);
      }
    }, 1500);
  }, [projectId, hasToken]);

  useEffect(() => {
    // Load saved loglines from API with localStorage fallback
    const demoSession = !hasToken && projectId === 'landing';
    
    const loadLoglinesData = async () => {
      if (demoSession) {
        localStorage.removeItem(`loglines-${projectId}`);
        setLoglines([]);
        isLoadedRef.current = true;
        return;
      }
      
      try {
        const response = await getLoglines(projectId);
        if (response?.data?.loglines?.loglines) {
          setLoglines(response.data.loglines.loglines);
          console.log('✅ Loaded loglines from API');
          localStorage.setItem(`loglines-${projectId}`, JSON.stringify(response.data.loglines.loglines));
        } else {
          throw new Error('No data from API');
        }
      } catch (err) {
        console.log('🔍 No API data, checking localStorage...');
        try {
          const savedLoglines = localStorage.getItem(`loglines-${projectId}`);
          if (savedLoglines) {
            const parsed = JSON.parse(savedLoglines);
            setLoglines(parsed);
            // Migrate to API
            setTimeout(() => {
              saveLoglines(projectId, { loglines: parsed })
                .then(() => console.log('✅ Migrated loglines to API'))
                .catch(e => console.log('Migration pending:', e.message));
            }, 2000);
          }
        } catch (localErr) {
          console.error('Error loading from localStorage:', localErr);
        }
      }
      isLoadedRef.current = true;
    };
    
    loadLoglinesData();
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [projectId, hasToken]);

  // Keep hasToken in sync if auth changes (e.g., login/logout in another tab)
  useEffect(() => {
    const handler = () => setHasToken(!!localStorage.getItem('token'));
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Keep AI tab available; guide user to login when generating without token

  // Local demo generator for unauthenticated users (simulate AI variations)
  const generateLocalDemo = () => {
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    // Fill blanks with random suggestions to make outputs feel rich
    const protagonist = currentLogline.protagonist || pick(PROTAGONIST_OPTIONS).label || 'A reluctant hero';
    const antagonist = currentLogline.antagonist || (typeof pick(ANTAGONIST_OPTIONS) === 'string' ? pick(ANTAGONIST_OPTIONS) : pick(ANTAGONIST_OPTIONS).label);
    const action = currentLogline.action || pick(ACTION_OPTIONS);
    const goal = currentLogline.goal || pick(GOAL_OPTIONS);
    const stakes = currentLogline.stakes || pick(STAKES_OPTIONS);
    const incident = currentLogline.incitingIncident || pick(INCIDENT_OPTIONS);
    const location = currentLogline.location || pick(LOCATION_OPTIONS);

    // Small pool of phrase variations
    const whenPhrases = ['When', 'After', 'Following', 'As'];
    const mustPhrases = ['must', 'is forced to', 'has to', 'is compelled to'];
    const againstPhrases = ['against', 'while facing', 'as they confront', 'opposed by'];
    const toPhrases = ['to', 'in order to'];
    const orElsePhrases = [', or else', ', otherwise', ', or'];

    const variations = Array.from({ length: 3 }).map((_, i) => {
      let text;
      if (formula === 'f1') {
        text = `${pick(whenPhrases)} ${incident.toLowerCase()}, ${protagonist.toLowerCase()} ${pick(mustPhrases)} ${action}${antagonist ? ` ${pick(againstPhrases)} ${antagonist.toLowerCase()}` : ''}.`;
      } else {
        const maybeGoal = goal ? ` ${pick(toPhrases)} ${goal.toLowerCase()}` : '';
        const maybeStakes = stakes ? `${pick(orElsePhrases)} ${stakes.toLowerCase()}` : '';
        const maybeAnt = antagonist ? ` ${pick(againstPhrases)} ${antagonist.toLowerCase()}` : '';
        text = `${protagonist} ${pick(mustPhrases)} ${action}${maybeAnt}${maybeGoal}${maybeStakes}.`;
      }
      // Occasionally add location for flavor
      if (Math.random() < 0.4) {
        text = `${text.slice(0, -1)} set in ${location.toLowerCase()}.`;
      }
      return {
        id: Date.now() + i,
        text,
        elements: {
          genre: currentLogline.genre,
          protagonist,
          action,
          goal,
          antagonist,
          conflict: currentLogline.conflict,
          stakes,
          location,
          incitingIncident: incident,
          formula
        },
        favorite: false,
        demo: true
      };
    });
    setLoglines([...variations, ...loglines]);
    // Do NOT persist demo results
  };

  const generateLogline = async () => {
    setLoading(true);
    setError(null);

    try {
      const userToken = localStorage.getItem('token');
      if (!userToken) {
        // Anonymous demo: generate locally to let users try AI experience
        generateLocalDemo();
        setSnackbar({ open: true, message: 'Demo results generated locally. Sign in for AI-powered variations.', severity: 'info' });
        return; // Skip server call
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      const isMisconfiguredLocalhostBase =
        !isLocalhost &&
        !!apiBaseUrl &&
        (apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1'));

      const endpoint = (!apiBaseUrl || isMisconfiguredLocalhostBase)
        ? '/api/ai/generate-logline'
        : `${apiBaseUrl.replace(/\/$/, '')}/api/ai/generate-logline`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          genre: currentLogline.genre,
          protagonist: currentLogline.protagonist,
          antagonist: currentLogline.antagonist,
          incitingIncident: currentLogline.incitingIncident,
          action: currentLogline.action,
          goal: currentLogline.goal,
          stakes: currentLogline.stakes,
          location: currentLogline.location,
          formula
        })
      });

      if (!response.ok) {
        // Try to extract the server's error message for a more helpful UI
        let serverMessage = '';
        try {
          const errorBody = await response.json();
          serverMessage = errorBody?.error || errorBody?.message || '';
        } catch (_) { /* response body wasn't JSON */ }

        if (response.status === 401) {
          throw new Error('Unauthorized: Please log in again.');
        } else if (response.status === 429) {
          throw new Error('Rate limit reached. Please wait a moment and try again.');
        } else if (response.status === 503 || response.status === 502) {
          throw new Error('The AI service is temporarily unavailable. Please try again shortly.');
        } else if (response.status === 500) {
          throw new Error(serverMessage || 'The AI service encountered an error. If this persists, the API key may be unconfigured or the AI provider may be down.');
        } else {
          throw new Error(serverMessage || `Server error (${response.status}). Please try again.`);
        }
      }

      const result = await response.json();
      if (!result || !result.loglines) {
        throw new Error('Invalid response from server');
      }

      const generatedLoglines = result.loglines.map((text, index) => ({
        id: Date.now() + index,
        text: text.trim(),
        elements: {
          genre: currentLogline.genre,
          protagonist: currentLogline.protagonist,
          action: currentLogline.action,
          goal: currentLogline.goal,
          antagonist: currentLogline.antagonist,
          conflict: currentLogline.conflict,
          stakes: currentLogline.stakes,
          location: currentLogline.location,
          incitingIncident: currentLogline.incitingIncident,
          formula
        },
        favorite: false
      }));

      const updatedLoglines = [...generatedLoglines, ...loglines];
      setLoglines(updatedLoglines);
      saveToAPI(updatedLoglines);
    } catch (error) {
      console.error('Error generating loglines:', error);
      if (error instanceof TypeError && String(error.message).toLowerCase().includes('fetch')) {
        setError('Unable to reach the API server. Make sure the backend is running and accessible.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSimpleLogline = () => {
    const protagonist = currentLogline.protagonist || 'Someone';
    const antagonist = currentLogline.antagonist || 'an opposing force';
    const action = currentLogline.action || 'take decisive action';
    const goal = currentLogline.goal || '';
    const stakes = currentLogline.stakes || '';
    const incident = currentLogline.incitingIncident || 'an unexpected event';

    let logline;
    if (formula === 'f1') {
      // Inciting Incident, Protagonist, Action, Antagonist
      // Example: When an unexpected event occurs, a young detective must uncover the truth against a powerful crime lord.
      logline = `When ${incident}, ${protagonist} must ${action}${antagonist ? ` against ${antagonist}` : ''}.`;
    } else {
      // Protagonist, Action, Antagonist, Goal, Stakes
      // Example: A young detective must uncover the truth against a powerful crime lord to solve the case, or else lose their career.
      const goalPart = goal ? ` to ${goal}` : '';
      const stakesPart = stakes ? `, or else ${stakes}` : '';
      const antagonistPart = antagonist ? ` against ${antagonist}` : '';
      logline = `${protagonist} must ${action}${antagonistPart}${goalPart}${stakesPart}.`;
    }
    const newLogline = {
      id: Date.now(),
      text: logline,
      elements: {
        genre: currentLogline.genre,
        protagonist,
        action,
        goal: currentLogline.goal,
        antagonist,
        conflict: currentLogline.conflict,
        stakes: currentLogline.stakes,
        location: currentLogline.location,
        incitingIncident: incident,
        formula
      },
      favorite: false
    };
    const updatedLoglines = [newLogline, ...loglines];
    setLoglines(updatedLoglines);
    saveToAPI(updatedLoglines);
  };

  const toggleFavorite = (id) => {
    const updatedLoglines = loglines.map(logline =>
      logline.id === id ? { ...logline, favorite: !logline.favorite } : logline
    );
    setLoglines(updatedLoglines);
    saveToAPI(updatedLoglines);
  };

  const deleteLogline = (id) => {
    const updatedLoglines = loglines.filter(logline => logline.id !== id);
    setLoglines(updatedLoglines);
    saveToAPI(updatedLoglines);
    setSnackbar({ open: true, message: 'Logline deleted', severity: 'info' });
  };

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to copy', severity: 'error' });
    }
  };

  

  const displayedLoglines = (showFavoritesOnly ? loglines.filter(l => l.favorite) : [...loglines].sort((a,b) => (b.favorite === a.favorite) ? (b.id - a.id) : (b.favorite - a.favorite)));

  

  const handleClearAll = () => {
    setConfirmClearOpen(true);
  };

  const confirmClearAll = () => {
    setConfirmClearOpen(false);
    setLoglines([]);
    saveToAPI([]);
    setSnackbar({ open: true, message: 'All loglines cleared', severity: 'info' });
  };

  const cancelClearAll = () => setConfirmClearOpen(false);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: 3 }}>
        {/* Modern Header with Gradient Badge */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <RefreshIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Logline Generator
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {loglines.length} loglines generated
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Clear all loglines">
              <Button variant="outlined" size="small" color="error" startIcon={<ClearAllIcon />} onClick={handleClearAll} sx={{ textTransform: 'none' }}>
                Clear all
              </Button>
            </Tooltip>
          </Box>
        </Box>


        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Formula</Typography>
            <ToggleButtonGroup
              value={formula}
              exclusive
              size="small"
              onChange={handleFormulaChange}
              sx={{ ml: 1 }}
            >
              <ToggleButton value="f1">Incident → Protagonist → Action → Antagonist</ToggleButton>
              <ToggleButton value="f2">Protagonist → Action → Antagonist → Goal → Stakes</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="scrollable"
          sx={{
            mb: 1,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 2,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 44,
            }
          }}
        >
          <Tab label="Simple Mode" value="simple" />
          <Tab label="AI Mode" value="ai" />
        </Tabs>

        {mode === 'ai' && !hasToken && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You are viewing a demo of AI mode. Results are generated locally. Sign in to use the full AI generator.
          </Alert>
        )}

        {!hasToken && projectId === 'landing' && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Demo mode: results are not saved and will clear on refresh.
          </Typography>
        )}

        {mode === 'simple' && (
          <SimpleMode
            currentLogline={currentLogline}
            setCurrentLogline={setCurrentLogline}
            generateLogline={generateSimpleLogline}
            loading={loading}
            formula={formula}
          />
        )}

        {mode === 'ai' && (
          <AIMode
            currentLogline={currentLogline}
            setCurrentLogline={setCurrentLogline}
            generateLogline={generateLogline}
            loading={loading}
            error={error}
          />
        )}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
            }}
          >
            <StarIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Generated Loglines</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Favorites only"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {displayedLoglines.length === 0 ? (
          <Box>
            <Paper
              variant="outlined"
              sx={{ p: 3, textAlign: 'center', borderRadius: 2, color: 'text.secondary' }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                {loglines.length === 0 ? 'No loglines yet. Try generating one!' : 'No favorites yet. Mark some with the star.'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => setMode('ai')}
                sx={{ textTransform: 'none' }}
              >
                Go to Generator
              </Button>
            </Paper>
          </Box>
        ) : (
          displayedLoglines.map((logline) => (
            <Paper
              key={logline.id}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 1,
                border: theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.divider, 0.3)}`
                  : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? alpha('#2a2a2a', 0.8)
                  : '#fff',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(0,0,0,0.4)'
                  : '0 1px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha('#3a3a3a', 0.9)
                    : '#fafafa',
                  boxShadow: theme.palette.mode === 'dark' 
                    ? '0 8px 24px rgba(0,0,0,0.5)' 
                    : '0 6px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                  borderColor: alpha(theme.palette.primary.main, 0.4)
                }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.05rem',
                    lineHeight: 1.7,
                    fontWeight: 500,
                    color: theme.palette.mode === 'dark'
                      ? alpha('#ffffff', 0.95)
                      : theme.palette.text.primary
                  }}
                >
                  {logline.text}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="Copy">
                  <IconButton 
                    onClick={() => handleCopy(logline.text)} 
                    size="small"
                    sx={{
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={logline.favorite ? 'Unfavorite' : 'Favorite'}>
                  <IconButton
                    onClick={() => toggleFavorite(logline.id)}
                    size="small"
                    sx={{
                      color: logline.favorite ? theme.palette.primary.main : theme.palette.text.secondary,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <StarIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton 
                    onClick={() => deleteLogline(logline.id)} 
                    size="small"
                    sx={{
                      color: theme.palette.error.main,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog 
        open={confirmClearOpen} 
        onClose={cancelClearAll}
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>Clear all loglines?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all generated loglines from this project on this device. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClearAll}>Cancel</Button>
          <Button onClick={confirmClearAll} color="error" variant="contained">Clear all</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const SimpleMode = ({ currentLogline, setCurrentLogline, generateLogline, loading, formula }) => {
  // Inline typing enabled via Autocomplete (freeSolo), no conditional fields needed

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Render order depends on formula */}

        {/* f1: Incident → Protagonist → Action → Antagonist */}
        {formula === 'f1' && (
          <>
            {/* Inciting Incident */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={INCIDENT_OPTIONS}
                value={currentLogline.incitingIncident}
                onChange={(e, newValue) => setCurrentLogline({ ...currentLogline, incitingIncident: newValue || '' })}
                onInputChange={(e, newInputValue) => setCurrentLogline({ ...currentLogline, incitingIncident: newInputValue || '' })}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Inciting Incident" 
                    helperText="What triggers the story? (used in this formula)" 
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Protagonist */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[
                  // Archetypes
                  { label: 'A reluctant hero', group: 'Archetypes' },
                  { label: 'An anti-hero', group: 'Archetypes' },
                  { label: 'A chosen one', group: 'Archetypes' },
                  { label: 'A fish-out-of-water', group: 'Archetypes' },
                  { label: 'A cynical veteran', group: 'Archetypes' },
                  { label: 'An idealistic rookie', group: 'Archetypes' },
                  { label: 'A misfit outsider', group: 'Archetypes' },
                  { label: 'A flawed genius', group: 'Archetypes' },
                  { label: 'A single parent', group: 'Archetypes' },
                  { label: 'A retired legend', group: 'Archetypes' },
                  // Professions
                  { label: 'A young detective', group: 'Professions' },
                  { label: 'A struggling artist', group: 'Professions' },
                  { label: 'A brilliant scientist', group: 'Professions' },
                  { label: 'A small-town sheriff', group: 'Professions' },
                  { label: 'An investigative journalist', group: 'Professions' },
                  { label: 'A washed-up athlete', group: 'Professions' },
                  { label: 'An ambitious lawyer', group: 'Professions' },
                  { label: 'A compassionate doctor', group: 'Professions' },
                  { label: 'A burned-out teacher', group: 'Professions' },
                  { label: 'A hacker-for-hire', group: 'Professions' },
                  // Diverse & Non-Human
                  { label: 'An AI companion', group: 'Non-Human' },
                  { label: 'A sentient android', group: 'Non-Human' },
                  { label: 'An orphaned street kid', group: 'Background' },
                  { label: 'A refugee with a secret', group: 'Background' },
                  { label: 'A royal in disguise', group: 'Background' }
                ]}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                groupBy={(option) => typeof option === 'string' ? 'Suggestions' : option.group}
                isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.label) === (typeof val === 'string' ? val : val?.label)}
                value={currentLogline.protagonist}
                onChange={(e, newValue) => setCurrentLogline({ ...currentLogline, protagonist: typeof newValue === 'string' ? newValue : (newValue?.label || '') })}
                onInputChange={(e, newInputValue) => setCurrentLogline({ ...currentLogline, protagonist: newInputValue || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Protagonist"
                    helperText="Type freely or pick an archetype/profession"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Action */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={ACTION_OPTIONS}
                value={currentLogline.action}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Action" placeholder="e.g., uncover the truth" helperText="What must the protagonist do?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>

            {/* Antagonist */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={ANTAGONIST_OPTIONS}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                groupBy={(option) => typeof option === 'string' ? 'Suggestions' : option.group}
                isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.label) === (typeof val === 'string' ? val : val?.label)}
                value={currentLogline.antagonist}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: typeof v === 'string' ? v : (v?.label || '') })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Antagonist" helperText="Primary opposing force" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>
          </>
        )}

        {/* f2: Protagonist → Action → Antagonist → Goal → Stakes */}
        {formula === 'f2' && (
          <>
            {/* Protagonist */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[
                  // Archetypes and professions (same list)
                  { label: 'A reluctant hero', group: 'Archetypes' },
                  { label: 'An anti-hero', group: 'Archetypes' },
                  { label: 'A chosen one', group: 'Archetypes' },
                  { label: 'A fish-out-of-water', group: 'Archetypes' },
                  { label: 'A cynical veteran', group: 'Archetypes' },
                  { label: 'An idealistic rookie', group: 'Archetypes' },
                  { label: 'A misfit outsider', group: 'Archetypes' },
                  { label: 'A flawed genius', group: 'Archetypes' },
                  { label: 'A single parent', group: 'Archetypes' },
                  { label: 'A retired legend', group: 'Archetypes' },
                  { label: 'A young detective', group: 'Professions' },
                  { label: 'A struggling artist', group: 'Professions' },
                  { label: 'A brilliant scientist', group: 'Professions' },
                  { label: 'A small-town sheriff', group: 'Professions' },
                  { label: 'An investigative journalist', group: 'Professions' },
                  { label: 'A washed-up athlete', group: 'Professions' },
                  { label: 'An ambitious lawyer', group: 'Professions' },
                  { label: 'A compassionate doctor', group: 'Professions' },
                  { label: 'A burned-out teacher', group: 'Professions' },
                  { label: 'A hacker-for-hire', group: 'Professions' },
                  { label: 'An AI companion', group: 'Non-Human' },
                  { label: 'A sentient android', group: 'Non-Human' },
                  { label: 'An orphaned street kid', group: 'Background' },
                  { label: 'A refugee with a secret', group: 'Background' },
                  { label: 'A royal in disguise', group: 'Background' }
                ]}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                groupBy={(option) => typeof option === 'string' ? 'Suggestions' : option.group}
                isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.label) === (typeof val === 'string' ? val : val?.label)}
                value={currentLogline.protagonist}
                onChange={(e, newValue) => setCurrentLogline({ ...currentLogline, protagonist: typeof newValue === 'string' ? newValue : (newValue?.label || '') })}
                onInputChange={(e, newInputValue) => setCurrentLogline({ ...currentLogline, protagonist: newInputValue || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Protagonist" helperText="Type freely or pick an archetype/profession" fullWidth />
                )}
              />
            </Grid>

            {/* Action */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[
                  'uncover the truth',
                  'stop the villain',
                  'save their family',
                  'win the championship',
                  'escape the city',
                  'find the missing person',
                  'protect the secret',
                  'assemble a team'
                ]}
                value={currentLogline.action}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Action" placeholder="e.g., uncover the truth" helperText="What must the protagonist do?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>

            {/* Antagonist */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[ 'A powerful crime lord','A corrupt government official','A rival genius','An ancient evil' ]}
                value={currentLogline.antagonist}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: v || '' })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Antagonist" helperText="Primary opposing force" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>

            {/* Goal */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[
                  'Solve the case',
                  'Win the competition',
                  'Rescue a loved one',
                  'Escape a dangerous situation',
                  'Uncover the truth',
                  'Survive against the odds',
                  'Protect their family',
                  'Avenge a wrongdoing',
                  'Save the world',
                  'Prove their innocence',
                  'Reclaim their legacy',
                  'Find a missing person',
                  'Stop a looming catastrophe',
                  'Achieve a lifelong dream'
                ]}
                value={currentLogline.goal}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, goal: v || '' })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, goal: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Goal" placeholder="e.g., Solve the case" helperText="What must the protagonist achieve?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>

            {/* Stakes */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[
                  'Lose their career',
                  'Lose their family',
                  'Go to prison',
                  'Lose their life',
                  'Reputation ruined',
                  'Lose their freedom',
                  'Financial ruin',
                  'Exile or banishment',
                  'Expose a devastating secret',
                  'City destroyed',
                  'Apocalypse',
                  'Lose true love',
                  'Legacy tarnished',
                  'Community collapses'
                ]}
                value={currentLogline.stakes}
                onChange={(e, v) => setCurrentLogline({ ...currentLogline, stakes: v || '' })}
                onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, stakes: v || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Stakes" placeholder="e.g., Lose their career" helperText="What happens if they fail?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
                )}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={generateLogline}
            fullWidth
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? 'Generating...' : 'Generate Logline'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const AIMode = ({ currentLogline, setCurrentLogline, generateLogline, loading, error }) => {
  const [showProtagonistText, setShowProtagonistText] = useState(false);
  const [showAntagonistText, setShowAntagonistText] = useState(false);
  const [showIncidentText, setShowIncidentText] = useState(false);
  const [showLocationText, setShowLocationText] = useState(false);

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Genre */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={GENRE_OPTIONS}
            value={currentLogline.genre}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, genre: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, genre: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Genre" helperText="Select or type a genre" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Location */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={LOCATION_OPTIONS}
            value={currentLogline.location}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, location: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, location: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Location" helperText="Where the story primarily takes place" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Protagonist */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={PROTAGONIST_OPTIONS}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            groupBy={(option) => typeof option === 'string' ? 'Suggestions' : option.group}
            isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.label) === (typeof val === 'string' ? val : val?.label)}
            value={currentLogline.protagonist}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, protagonist: typeof v === 'string' ? v : (v?.label || '') })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, protagonist: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Protagonist" helperText="Main character description" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Action */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={ACTION_OPTIONS}
            value={currentLogline.action}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, action: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Action" helperText="What must the protagonist do?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Antagonist */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={ANTAGONIST_OPTIONS}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            groupBy={(option) => typeof option === 'string' ? 'Suggestions' : option.group}
            isOptionEqualToValue={(opt, val) => (typeof opt === 'string' ? opt : opt.label) === (typeof val === 'string' ? val : val?.label)}
            value={currentLogline.antagonist}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: typeof v === 'string' ? v : (v?.label || '') })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, antagonist: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Antagonist" helperText="Primary opposing force" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Inciting Incident */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={INCIDENT_OPTIONS}
            value={currentLogline.incitingIncident}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, incitingIncident: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, incitingIncident: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Inciting Incident" helperText="The spark that sets the plot in motion" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Goal */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={GOAL_OPTIONS}
            value={currentLogline.goal}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, goal: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, goal: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Goal" helperText="What must the protagonist achieve?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        {/* Stakes */}
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            size="small"
            options={STAKES_OPTIONS}
            value={currentLogline.stakes}
            onChange={(e, v) => setCurrentLogline({ ...currentLogline, stakes: v || '' })}
            onInputChange={(e, v) => setCurrentLogline({ ...currentLogline, stakes: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Stakes" helperText="What happens if they fail?" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#000', 0.4) : '#fff' } }} />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={generateLogline}
            fullWidth
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? 'Generating...' : 'Generate Logline'}
          </Button>
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoglineGenerator;
